import { Check, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { listDocumentScriptsSorted, type DocumentScript } from "@/document/model/documentScripts";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { cn } from "@/lib/utils";
import { DraggablePanel } from "./DraggablePanel";

const CREATE_SENTINEL = "__create__";

export function ScriptAttachPicker({
  appliedScriptId,
  onCreateNew,
  onCommit,
  onClose,
}: {
  appliedScriptId?: string;
  onCreateNew: () => void;
  onCommit: (scriptId: string) => void;
  onClose: () => void;
}) {
  const document = useStoreSubscribe(documentStore.document);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [query, setQuery] = useState("");
  const [highlightedId, setHighlightedId] = useState<string>(appliedScriptId ?? CREATE_SENTINEL);
  const onCreateNewRef = useRef(onCreateNew);
  const onCommitRef = useRef(onCommit);
  const onCloseRef = useRef(onClose);
  const highlightedIdRef = useRef(highlightedId);

  onCreateNewRef.current = onCreateNew;
  onCommitRef.current = onCommit;
  onCloseRef.current = onClose;
  highlightedIdRef.current = highlightedId;

  const scripts = useMemo(() => {
    const all = listDocumentScriptsSorted(document.scripts);
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return all;
    }
    return all.filter(script => script.name.toLowerCase().includes(trimmed));
  }, [document.scripts, query]);

  const rowIds = useMemo(() => [CREATE_SENTINEL, ...scripts.map(script => script.id)], [scripts]);

  useEffect(() => {
    searchRef.current?.focus();
    searchRef.current?.select();
    setHighlightedId(appliedScriptId ?? CREATE_SENTINEL);
  }, [appliedScriptId]);

  useEffect(() => {
    if (!rowIds.includes(highlightedIdRef.current)) {
      setHighlightedId(rowIds[0] ?? CREATE_SENTINEL);
    }
  }, [rowIds]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || panelRef.current?.contains(target)) {
        return;
      }
      onCloseRef.current();
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.target instanceof HTMLInputElement && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
        return;
      }

      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        commitHighlighted();
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <DraggablePanel
      panelRef={panelRef}
      storageKey="script-attach-picker"
      title="Attach script"
      widthClass="w-72"
      onClose={onClose}
    >
      <div className="space-y-2">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              moveHighlight(event.key === "ArrowDown" ? 1 : -1);
            }
          }}
          placeholder="Search scripts"
          className="h-8 w-full rounded border border-white/10 bg-white/[0.04] px-2 text-[12px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-500/40"
        />
        <div ref={listRef} className="max-h-72 overflow-y-auto rounded border border-white/10">
          <button
            type="button"
            ref={element => {
              if (element) {
                rowRefs.current.set(CREATE_SENTINEL, element);
              } else {
                rowRefs.current.delete(CREATE_SENTINEL);
              }
            }}
            onMouseEnter={() => setHighlightedId(CREATE_SENTINEL)}
            onClick={() => onCreateNew()}
            className={cn(
              "sticky top-0 z-10 flex h-9 w-full items-center gap-2 border-b border-white/10 bg-[#242a31] px-2 text-left text-[12px] text-slate-200 hover:bg-white/[0.06]",
              highlightedId === CREATE_SENTINEL && "bg-sky-500/20 text-sky-100",
            )}
          >
            <Plus className="size-3.5 shrink-0 text-sky-300" aria-hidden="true" />
            <span className="truncate">Create new script</span>
          </button>
          {scripts.length === 0 ? (
            <div className="px-2 py-3 text-center text-[11px] text-slate-500">No matching scripts.</div>
          ) : (
            scripts.map(script => (
              <ScriptAttachRow
                key={script.id}
                script={script}
                applied={script.id === appliedScriptId}
                highlighted={script.id === highlightedId}
                rowRefs={rowRefs}
                onHighlight={() => setHighlightedId(script.id)}
                onCommit={() => onCommit(script.id)}
              />
            ))
          )}
        </div>
      </div>
    </DraggablePanel>
  );

  function moveHighlight(delta: number): void {
    if (rowIds.length === 0) {
      return;
    }
    const currentIndex = Math.max(0, rowIds.indexOf(highlightedId));
    const nextIndex = Math.min(rowIds.length - 1, Math.max(0, currentIndex + delta));
    const nextId = rowIds[nextIndex];
    if (!nextId) {
      return;
    }
    setHighlightedId(nextId);
    requestAnimationFrame(() => {
      const row = rowRefs.current.get(nextId);
      const list = listRef.current;
      if (!row || !list) {
        return;
      }
      const rowTop = row.offsetTop;
      const rowBottom = rowTop + row.offsetHeight;
      if (rowTop < list.scrollTop) {
        list.scrollTop = rowTop;
      } else if (rowBottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = rowBottom - list.clientHeight;
      }
    });
  }

  function commitHighlighted(): void {
    if (highlightedIdRef.current === CREATE_SENTINEL) {
      onCreateNewRef.current();
      return;
    }
    onCommitRef.current(highlightedIdRef.current);
  }
}

function ScriptAttachRow({
  script,
  applied,
  highlighted,
  rowRefs,
  onHighlight,
  onCommit,
}: {
  script: DocumentScript;
  applied: boolean;
  highlighted: boolean;
  rowRefs: MutableRefObject<Map<string, HTMLButtonElement>>;
  onHighlight: () => void;
  onCommit: () => void;
}) {
  return (
    <button
      type="button"
      ref={element => {
        if (element) {
          rowRefs.current.set(script.id, element);
        } else {
          rowRefs.current.delete(script.id);
        }
      }}
      onMouseEnter={onHighlight}
      onClick={onCommit}
      className={cn(
        "flex h-9 w-full items-center gap-2 px-2 text-left text-[12px] text-slate-200 hover:bg-white/[0.06]",
        highlighted && "bg-sky-500/20 text-sky-100",
      )}
    >
      <span className="grid size-4 shrink-0 place-items-center text-sky-300">
        {applied ? <Check className="size-3.5" aria-hidden="true" /> : null}
      </span>
      <span className="min-w-0 truncate">{script.name}</span>
    </button>
  );
}
